import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import CompetitiveSessionPage from './CompetitiveSessionPage';
import SocialSessionPage from './SocialSessionPage';
import PrivateSessionDashboard from './PrivateSessionDashboard';
import { SeasonProvider } from '../context/SeasonContext';
import { BackButton } from '../components/common';
import { APP_PAGE_BLEED_ROOT, SESSION_PAGE_GUTTER } from '../utils/sessionMobileLayout';
import { isPrivateSession, isSocialSession as checkIsSocialSession, isPegBoardSession } from '../utils/sessionTypeUtils';

function SessionDashboard({ currentUser }) {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [selectedClub, setSelectedClub] = useState(null);
  const [isSessionHost, setIsSessionHost] = useState(true); // default true so existing flows unchanged until session loads
  const [canAccessSessionManagement, setCanAccessSessionManagement] = useState(true); // true for host/admin so back goes to start-session; false for participants -> home
  const [isClubAdmin, setIsClubAdmin] = useState(false); // Track if user is club admin/owner/host
  const [sessionCreator, setSessionCreator] = useState(null); // Track session creator info
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileHeaderAction, setMobileHeaderAction] = useState(null);

  const registerMobileHeaderAction = useCallback((node) => {
    setMobileHeaderAction(node);
  }, []);
  
  // Track if fetch is already in progress to prevent multiple calls
  const fetchInProgressRef = useRef(false);

  // Fetch session details, check access (host or participant), and set isSessionHost
  useEffect(() => {
    const fetchSessionDetails = async () => {
      if (!sessionId) return;
      
      // Prevent multiple simultaneous fetches
      if (fetchInProgressRef.current) {
        console.log('⏭️ SessionDashboard: Fetch already in progress, skipping');
        return;
      }
      
      fetchInProgressRef.current = true;

      try {
        setLoading(true);
        setError(null);

        // Fetch session details (sessions.* includes created_by)
        const { data: sessionData, error: sessionError } = await supabase
          .from('sessions')
          .select(`
            *,
            session_types:session_type_id (
              id,
              name,
              description
            ),
            clubs:club_id (
              id,
              name,
              description
            ),
            creator:created_by (
              id,
              first_name,
              last_name
            )
          `)
          .eq('id', sessionId)
          .single();

        if (sessionError) {
          console.error('Error fetching session:', sessionError);
          setError('Session not found or access denied');
          return;
        }

        if (!sessionData) {
          setError('Session not found');
          return;
        }

        // Store creator info
        setSessionCreator(sessionData.creator);

        // Check if this is a private session and route accordingly
        if (isPrivateSession(sessionData)) {
          setSession(sessionData);
          setSelectedClub(sessionData.clubs);
          const isHost = sessionData.host_player_id === currentUser?.id;
          setIsSessionHost(isHost);
          setCanAccessSessionManagement(isHost);
          return;
        }

        // Check club membership and role first
        const clubId = sessionData.club_id;
        let clubRole = null;
        let isAdmin = false;

        if (clubId && currentUser?.id) {
          const { data: membership } = await supabase
            .from('players_club')
            .select('club_role')
            .eq('player_id', currentUser.id)
            .eq('club_id', clubId)
            .single();

          if (membership) {
            clubRole = membership.club_role;
            isAdmin = ['host', 'admin', 'owner'].includes(membership.club_role);
            setIsClubAdmin(isAdmin);
          }
        }

        // Club sessions (competitive/social): allow host or any player in session_players
        const isCreator = sessionData.created_by === currentUser?.id;
        const isDesignatedHost = sessionData.host_player_id === currentUser?.id;
        
        // Session creator, designated host, or club admin have full host access
        if (isCreator || isDesignatedHost || isAdmin) {
          setSession(sessionData);
          setSelectedClub(sessionData.clubs);
          setIsSessionHost(true);
          setCanAccessSessionManagement(true);
          return;
        }

        if (!currentUser?.id) {
          setError('Please sign in to view this session');
          return;
        }

        // Check if user is a participant
        const { data: participantRows, error: participantError } = await supabase
          .from('session_players')
          .select('id')
          .eq('session_id', sessionId)
          .eq('player_id', currentUser.id)
          .limit(1);

        if (participantError || !participantRows?.length) {
          setError('You don’t have access to this session. Only the host and active players can view it.');
          return;
        }

        setSession(sessionData);
        setSelectedClub(sessionData.clubs);
        setIsSessionHost(false);
        setCanAccessSessionManagement(false); // participant only -> back goes to home
        setIsClubAdmin(false);
      } catch (err) {
        console.error('Error loading session:', err);
        setError('Failed to load session');
      } finally {
        setLoading(false);
        fetchInProgressRef.current = false;
      }
    };

    fetchSessionDetails();
  }, [sessionId, currentUser?.id]);

  // Handle navigation back: host/admin go to session management; participants go to home (they can't access start-session)
  const handleBackToSessions = useCallback(() => {
    if (canAccessSessionManagement && selectedClub?.id) {
      navigate(`/club/${selectedClub.id}/sessions/start`);
    } else {
      navigate('/');
    }
  }, [navigate, canAccessSessionManagement, selectedClub?.id]);

  const backButtonLabel = canAccessSessionManagement && selectedClub?.id ? 'Back to Start session' : 'Back to Home';

  // Memoize selectedClub to prevent Dashboard remounting
  const memoizedSelectedClub = useMemo(() => selectedClub, [selectedClub]);
  
  // Log prop changes to debug remounting
  console.log('🔄 SessionDashboard: Props changed - currentUser:', !!currentUser, 'selectedClub:', selectedClub?.id, 'sessionId:', sessionId);
  
  // Pass session and session type so Dashboard doesn't re-fetch
  const initialIsSocialSession = session ? checkIsSocialSession(session) : false;
  
  // Route private sessions to PrivateSessionDashboard
  if (session && isPrivateSession(session)) {
    return (
      <PrivateSessionDashboard
        currentUser={currentUser}
        sessionId={sessionId}
        sessionData={session}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="text-danger-dark text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-text-primary mb-2">Session Error</h2>
          <p className="text-text-secondary mb-4">{error}</p>
          <button
            onClick={handleBackToSessions}
            className="bg-primary text-text-inverse px-4 py-2 rounded-lg hover:bg-primary-dark"
          >
            {backButtonLabel}
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="text-text-secondary text-6xl mb-4">📋</div>
          <h2 className="text-2xl font-bold text-text-primary mb-2">Session Not Found</h2>
          <p className="text-text-secondary mb-4">The requested session could not be found.</p>
          <button
            onClick={handleBackToSessions}
            className="bg-primary text-text-inverse px-4 py-2 rounded-lg hover:bg-primary-dark"
          >
            {backButtonLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <SeasonProvider clubId={selectedClub?.id}>
      <div className={`min-h-screen bg-background max-w-app-shell mx-auto ${APP_PAGE_BLEED_ROOT} ${SESSION_PAGE_GUTTER}`}>
        {/* Back + mobile session actions (e.g. End session) on one row */}
        <div className="mb-4 flex items-center justify-between gap-2">
          <BackButton onClick={handleBackToSessions} className="min-w-0 shrink">
            {backButtonLabel}
          </BackButton>
          {mobileHeaderAction ? (
            <div className="shrink-0 md:hidden">{mobileHeaderAction}</div>
          ) : null}
        </div>


        {/* Session content: SocialSessionPage or Dashboard (competitive) */}
        {initialIsSocialSession ? (
          <SocialSessionPage
            currentUser={currentUser}
            selectedClub={memoizedSelectedClub}
            sessionId={sessionId}
            session={session}
            isSessionHost={isSessionHost}
            sessionCreator={sessionCreator}
            isClubAdmin={isClubAdmin}
          />
        ) : (
          <CompetitiveSessionPage
            currentUser={currentUser}
            selectedClub={memoizedSelectedClub}
            sessionId={sessionId}
            selectedSetup={null}
            isSessionHost={isSessionHost}
            initialSession={session}
            sessionCreator={sessionCreator}
            isClubAdmin={isClubAdmin}
            registerMobileHeaderAction={registerMobileHeaderAction}
            isPegBoardSession={session ? isPegBoardSession(session) : false}
          />
        )}
      </div>
    </SeasonProvider>
  );
}

export default SessionDashboard;
